/**
 * Completion tracking for a widget refresh.
 *
 * A refresh remounts a subtree. Every widget body inside it re-subscribes its
 * queries, and Apollo registers each as an active `ObservableQuery` on the
 * client. The queries the refresh created are therefore exactly the client's
 * active set minus the set that existed before the remount — which is how the
 * watcher covers every widget without any body's cooperation: a new widget, a
 * shared data hook, a dependent second query all surface the same way.
 *
 * Polling, not subscribing. Subscribing to an `ObservableQuery` adds an
 * observer, which keeps the query alive past its widget's unmount and can
 * change its fetch behavior; `getCurrentResult()` is side-effect free.
 */

export type RefreshSettleOutcome = "success" | "failed" | "timeout";

/** The slice of `ObservableQuery` the watcher reads. */
export interface RefreshQueryLike {
  getCurrentResult(): { loading: boolean; error?: unknown };
}

/** The slice of `ApolloClient` the watcher reads. */
export interface RefreshClientLike {
  getObservableQueries(include: "active"): Set<RefreshQueryLike>;
}

export interface RefreshSettleOptions {
  /** Poll spacing; each poll re-reads every watched query's current result. */
  pollMs?: number;
  /** Queries still in flight past this settle as `"timeout"` — no claim
   *  either way. */
  timeoutMs?: number;
}

/**
 * Watches the queries a refresh created until none is in flight, then reports
 * once: `"success"` when every one settled clean, `"failed"` when any settled
 * with an error, `"timeout"` when some were still pending at the deadline.
 * Returns a cancel function; a cancelled watcher never reports.
 *
 * `before` is the client's active set captured BEFORE the remount. Anything
 * active that is not in it is the refresh's — including queries that start
 * later in the window (a body that queries once its first result lands), which
 * every poll picks up. A watched query that leaves the active set (its widget
 * unmounted mid-refresh) is dropped rather than waited on.
 */
export function watchRefreshSettle(
  client: RefreshClientLike,
  before: ReadonlySet<RefreshQueryLike>,
  onDone: (outcome: RefreshSettleOutcome) => void,
  { pollMs = 100, timeoutMs = 30_000 }: RefreshSettleOptions = {},
): () => void {
  const startedAt = Date.now();
  const watched = new Set<RefreshQueryLike>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let finished = false;
  const finish = (outcome: RefreshSettleOutcome) => {
    finished = true;
    onDone(outcome);
  };
  const scan = () => {
    if (finished) return;
    const active = client.getObservableQueries("active");
    for (const query of active) {
      if (!before.has(query)) watched.add(query);
    }
    let pending = false;
    let failed = false;
    for (const query of watched) {
      if (!active.has(query)) {
        watched.delete(query);
        continue;
      }
      const result = query.getCurrentResult();
      if (result.loading) pending = true;
      else if (result.error) failed = true;
    }
    if (!pending) {
      finish(failed ? "failed" : "success");
      return;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      finish("timeout");
      return;
    }
    timer = setTimeout(scan, pollMs);
  };
  scan();
  return () => {
    finished = true;
    if (timer !== null) clearTimeout(timer);
  };
}
